import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setLoadTable } from '../../../redux/action/skuTable.action';
import { toast } from '../../../components/AppToast';
import { getSearchBySkuPO } from '../../../services/asnAPI/asn';
import { formatToDisplayDate, parseDisplayDate } from '../../../utils/dateFormatter'; // Added parseDisplayDate import
import CommonDateInput from '../../../components/CommonDateInput';
import FacilityAutoSearch from "../../../components/FacilityAutoSearch";
import PartnerAutoSearch from '../../../components/PartnerAutoSearch';
import PartnerLookupField from '../../../components/PartnerLookupField';
import LookupField from '../../../components/LookupField';
import { SUPPLIER_LOOKUP, CUSTOMER_LOOKUP, shipFromLookupForSupplier } from '../../../components/lookupConfigs';
import { prefillAsnFromPurchaseOrder } from '../formFields/mapPoToAsn';
import { isFieldVisible } from '../formFields/asnTabsFields';


const normalize = (v) =>
  String(v ?? "").trim().toLowerCase();


export default function GenericFormTab({ 
  fields, 
  formData, 
  onChange, 
  formSubmissionAttempted = false,
  readOnly = false // ✅ Accept readOnly prop
}) {
  const dispatch = useDispatch();
  const loadTable = useSelector(state => state.skutable.TableDetails);
  const userInteractingRef = useRef(false);
  const viewOnly = !!(formData && formData.__viewOnly) || readOnly; // ✅ Combine with prop

  useEffect(() => {
    if (formData.receiptStatus == null || formData.receiptStatus == '' || formData.receiptStatus === 'New') {
      onChange({ target: { name: 'receiptStatus', value: 'Pending' } });
    }
  }, [formData.receiptStatus, onChange]);

  // Remove the automatic prefetch of ASN numbers and other defaults
  useEffect(() => {
    const prefetchValues = async () => {
      // Only prefetch values that are not "createUsing" or dependent on user selection
      const updatedValues = {};
      let hasUpdates = false;

      // Remove automatic createUsing default setting
      // Keep this empty so user must manually select

      // Update form data with all prefetched values at once
      if (hasUpdates) {
        Object.entries(updatedValues).forEach(([name, value]) => {
          onChange({ target: { name, value } });
        });
      }
    };

    prefetchValues();
  }, [fields, formData, onChange]);

 




 


  const handleSearch = async (fieldName) => {
    if (viewOnly) return;
    userInteractingRef.current = true;

    // ---------- createUsing guard ----------
    const cu = Number(formData.createUsing);
    if (
      (fieldName === "SKUID" ||
        fieldName === "poNumber" ||
        fieldName === "externalPoNumber") &&
      cu
    ) {
      const blocked =
        (cu === 1 && fieldName !== "SKUID") ||
        (cu === 2 && fieldName !== "poNumber") ||
        (cu === 3 && fieldName !== "externalPoNumber");
      if (blocked) {
        userInteractingRef.current = false;
        return;
      }
    }

    // ---------- value ----------
    const raw =
      fieldName === "poNumber"
        ? formData.poNumber
        : fieldName === "SKUID"
          ? formData.SKUID
          : fieldName === "externalPoNumber"
            ? formData.externalPoNumber
            : "";

    const trimmed = (raw || "").toString().trim();
    if (!trimmed) {
      userInteractingRef.current = false;
      return;
    }

    const type = fieldName === "poNumber" ? 1 : 2; // 1=PO, 2=SKU
    const normalize = (v) => String(v || "").trim().toLowerCase();

    try {
      const apiResp = await getSearchBySkuPO(type, [trimmed]);
      const blocks = Array.isArray(apiResp?.data)
        ? apiResp.data
        : Array.isArray(apiResp)
          ? apiResp
          : [];

      if (!blocks.length) {
        toast.error(
          `No results found for ${type === 1 ? "PO Number" : "SKU ID"}: ${trimmed}`
        );
        userInteractingRef.current = false;
        return;
      }

      if (type === 1) {
        try {
          const prefill = await prefillAsnFromPurchaseOrder(blocks, onChange);
          if (prefill?.supplierMissing) {
            toast.warning("Supplier was not found, so the ship from list could not be loaded.");
          } else if (prefill?.shipFromCount > 1) {
            toast.info(
              `${prefill.shipFromCount} ship from locations found. The first is filled in. Open Ship From to choose another.`
            );
          }
        } catch (prefillErr) {
          console.error("ASN PO prefill failed:", prefillErr);
        }
      }

      const updated = { ...(loadTable || {}) };
      const pad4 = (n) => String(n).padStart(4, "0");

      let totalAdded = 0;
      let duplicateCount = 0;
      let lastGroupKey = null;

      // ---------- helper ----------
      const pushEnriched = (existingArr, r, idx, groupIdentifier, createUsing) => {
        const lineNumber = r.lineNumber || existingArr.length + idx + 1;

        return {
          ...r,
          lineNo: pad4(lineNumber),
          item: r.skuId || r.item || "",
          skuId: r.skuId || r.item || "",
          purchaseOrder: r.purchaseOrder || r.poNumber || groupIdentifier,
          poNumber: r.purchaseOrder || r.poNumber || groupIdentifier,
          description:
            r.description ||
            `Item ${r.skuId || r.item || groupIdentifier}`,
          pack: r.pack || "STD",
          uom: r.uom || "EA",
          expectedQty: r.orderedQty ?? r.expectedQty ?? 0,
          dispatchQty: r.dispatchQty ?? 0,
          shortQty: r.shortQty ?? 0,
          excessQty: r.excessQty ?? 0,
          damagedQty: r.damagedQty ?? 0,
          openQty: r.openQty ?? (r.orderedQty ?? 0),
          unitPrice: r.unitPrice ?? r.poUnitPrice ?? 0,
          discount: r.discount ?? r.poDiscount ?? 0,
          tax: r.tax ?? 0,
          asnUnitPrice: String(
            r.asnUnitPrice ?? r.unitPrice ?? r.poUnitPrice ?? ""
          ),
          asnDiscount: String(
            r.asnDiscount ?? r.discount ?? r.poDiscount ?? ""
          ),
          _createUsing: createUsing,
          itemCube: r.itemCube ?? null,
          itemWeight: r.itemWeight ?? null,
        };
      };

      // ---------- iterate blocks ----------
      for (const block of blocks) {
        const items = Array.isArray(block?.items) ? block.items : [];
        if (!items.length) continue;

        const createUsingFromServer = Number(block?.createUsing);
        const isPO =
          !isNaN(createUsingFromServer)
            ? createUsingFromServer === 1
            : type === 1;

        const rawKey =
          (block?.groupKey || "").toString().trim() || trimmed;

        const groupKey = isPO ? rawKey : "SKU::" + rawKey;

        const existingArr = Array.isArray(updated[groupKey])
          ? [...updated[groupKey]]
          : [];

        // ✅ FIXED DEDUPE LOGIC
        const perGroupSet = new Set(
          existingArr.map((r) =>
            normalize(
              isPO
                ? r.skuId || r.item
                : `${r.skuId || r.item}__${r.purchaseOrder || r.poNumber}`
            )
          )
        );

        const newRows = [];

        items.forEach((r, idx) => {
          const dedupeKey = normalize(
            isPO
              ? r.skuId || r.item
              : `${r.skuId || r.item}__${r.purchaseOrder || r.poNumber}`
          );

          if (!dedupeKey || perGroupSet.has(dedupeKey)) {
            duplicateCount++;
            return;
          }

          const enriched = pushEnriched(
            existingArr,
            r,
            idx,
            rawKey,
            isPO ? 2 : 1
          );

          newRows.push(enriched);
          perGroupSet.add(dedupeKey);
        });

        if (newRows.length > 0) {
          updated[groupKey] = [...existingArr, ...newRows];
          totalAdded += newRows.length;
          lastGroupKey = groupKey;
        }
      }

      // ---------- final guard ----------
      if (totalAdded === 0) {
        toast.warning(
          duplicateCount > 0
            ? `${duplicateCount} duplicate SKU(s) skipped. No new items added.`
            : "No new items to add."
        );
        userInteractingRef.current = false;
        return;
      }

      updated.__lastAddedTable = lastGroupKey;
      updated.__userTriggered = true;

      dispatch(setLoadTable(updated));
      toast.success(`Added ${totalAdded} item(s).`);
    } catch (err) {
      console.error("handleSearch error:", err);
      toast.error(
        "Failed to fetch data: " + (err?.message || "Unknown error")
      );
    } finally {
      setTimeout(() => {
        userInteractingRef.current = false;
      }, 600);
    }
  };




  // Handle date field conversions
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;

    // For date fields, convert from display format to ISO if needed
    if (type === 'date' && value && value.includes('/')) {
      const isoDate = parseDisplayDate(value);
      onChange({ target: { name, value: isoDate } });
    } else {
      onChange(e);
    }
  };

  const handlePartnerSelect = (fieldName, partner) => {
    if (!partner) return;

    onChange({
      target: {
        name: fieldName,
        value: partner?.partner_id || "",
      },
    });

    const addr = partner?.address || {};

    const isBuyer =
      fieldName === "owner" ||
      fieldName === "buyerName" ||
      fieldName === "buyer" ||
      fieldName === "consignee";
    const isSupplier =
      fieldName === "supplierName" ||
      fieldName === "supplierOwner" ||
      fieldName === "shipper";

    if (isBuyer) {
      const buyerMap = {
        buyerAddressLine1: addr.partner_address1 || "",
        buyerAddressLine2: addr.partner_address2 || "",
        buyerAddressLine3: "",
        buyerAddressLine4: "",
        buyerCity: addr.partner_city || "",
        buyerState: addr.partner_state || "",
        buyerCountry: addr.partner_country || "",
        buyerReference: partner.partner_name || "",
        buyerContact: partner.partner_mobile || "",
        buyerEmail: partner.partner_email || "",
      };
      Object.entries(buyerMap).forEach(([key, value]) =>
        onChange({ target: { name: key, value } })
      );
    }

    if (isSupplier) {
      const supplierMap = {
        supplierAddressLine1: addr.partner_address1 || "",
        supplierAddressLine2: addr.partner_address2 || "",
        supplierAddressLine3: "",
        supplierAddressLine4: "",
        supplierCity: addr.partner_city || "",
        supplierState: addr.partner_state || "",
        supplierCountry: addr.partner_country || "",
        supplierReference: partner.partner_name || "",
        supplierContact: partner.partner_mobile || "",
        supplierEmail: partner.partner_email || "",
      };
      Object.entries(supplierMap).forEach(([key, value]) =>
        onChange({ target: { name: key, value } })
      );
    }
  };

  const patch = (map) =>
    Object.entries(map).forEach(([name, value]) => onChange({ target: { name, value } }));

  const shipFromLookupConfig = useMemo(
    () => shipFromLookupForSupplier(formData.poSupplierId),
    [formData.poSupplierId]
  );

  /**
   * Supplier picked from `supplier/fetch-multi-dynamic`.
   *
   * `supplierId` (SKU ID / Blind ASN Ship From): only the supplier ID is set
   * and `poSupplierId` (supplier `_id`) scopes the Ship From Name lookup to that
   * supplier. The address is NOT copied from the supplier; it is filled when the
   * user picks a Ship From. Changing the supplier clears the previous Ship From.
   *
   * Any other supplier field: full supplier address prefill (legacy behaviour).
   */
  const applySupplier = (fieldName, rec) => {
    const s = SUPPLIER_LOOKUP.normalize(rec);

    if (fieldName === "supplierId") {
      const supplierChanged =
        String(formData.poSupplierId || "") !== String(s._id || "");
      patch({
        supplierId: s.id,
        poSupplierId: s._id,
        ...(supplierChanged
          ? {
              supplierName: "",
              supplierAddressLine1: "",
              supplierAddressLine2: "",
              supplierAddressLine3: "",
              supplierAddressLine4: "",
              supplierCity: "",
              supplierState: "",
              supplierCountry: "",
              supplierReference: "",
              supplierContact: "",
              supplierEmail: "",
            }
          : {}),
      });
      return;
    }

    patch({
      [fieldName]: s.name,
      supplierId: s.id,
      poSupplierId: s._id,
      supplierAddressLine1: s.address1,
      supplierAddressLine2: s.address2,
      supplierAddressLine3: "",
      supplierAddressLine4: "",
      supplierCity: s.city,
      supplierState: s.state,
      supplierCountry: s.country,
      supplierReference: s.id,
      supplierContact: s.mobile,
      supplierEmail: s.email,
    });
  };

  /** Ship From picked for the current supplier (PO supplier or `supplierId`) → Ship From section. */
  const applyShipFrom = (fieldName, rec) => {
    const s = shipFromLookupConfig.normalize(rec);
    patch({
      [fieldName]: s.name,
      supplierAddressLine1: s.address1,
      supplierAddressLine2: s.address2,
      supplierAddressLine3: "",
      supplierAddressLine4: "",
      supplierCity: s.city,
      supplierState: s.state,
      supplierCountry: s.country,
      supplierReference: s.id,
      supplierContact: s.mobile,
      supplierEmail: s.email,
    });
  };

  /** Customer picked from `customer-group/fetch-multi-dynamic` → Destination/Buyer section. */
  const applyCustomer = (fieldName, rec) => {
    const c = CUSTOMER_LOOKUP.normalize(rec);
    patch({
      [fieldName]: c.name,
      buyerAddressLine1: c.address1,
      buyerAddressLine2: c.address2,
      buyerAddressLine3: "",
      buyerAddressLine4: "",
      buyerCity: c.city,
      buyerState: c.state,
      buyerCountry: c.country,
      buyerReference: c.id,
      buyerContact: c.mobile,
      buyerEmail: c.email,
    });
  };

  // Add a list of date fields that should have date pickers
  const datePickerFields = [
    'expectedDepartureDate',
    'actualDepartureDate',
    'expectedArrivalDate',
    'actualArrivalDate',
    'invoiceDate',
    'closedDate',
    'loadingDate',
    'expectedReceiptDate'
  ];

  // Add a list of date-time fields that should show both date and time (read-only)
  const dateTimeFields = [
    'createdAt',
    'asnCreatedate',
    'purchaseOrderDate',
    'createDate',
    'editDate'
  ];

  const visibleFields = fields.filter((f) => isFieldVisible(f, formData));

  // An empty Blind ASN placeholder (`BLIND: []`) is not line data.
  // Lock Create Using only after a real SKU, PO, or Blind line exists.
  const hasTableData = Object.entries(loadTable || {}).some(
    ([key, rows]) =>
      !String(key).startsWith("__") && Array.isArray(rows) && rows.length > 0
  );

  // Handle file upload
  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Store file object for now - in production you'd upload to server
      onChange({
        target: {
          name: fieldName,
          value: file.name // Store filename for display
        }
      });

      // You can add file upload logic here
      // uploadFile(file).then(response => {
      //   onChange({ target: { name: fieldName, value: response.fileUrl } });
      // });
    }
  };

  return (
    <>
      <div className="row gx-2 mx-2">
        {visibleFields.map((field) => {
          if (field.hiddenOnCreate && !viewOnly) {
            return null;
          }

          const isRequired =
            field.required && !field.readOnly && !viewOnly;
          const fieldValue = formData[field.name];
          const isEmpty =
            fieldValue === null ||
            fieldValue === undefined ||
            fieldValue === "" ||
            (typeof fieldValue === "string" &&
              fieldValue.trim() === "");

          // Only show error styling if form submission was attempted AND field is required and empty
          const hasError =
            formSubmissionAttempted && isRequired && isEmpty;

          // Check if this is a date-time field or regular date field
          const isDateTimeField = dateTimeFields.includes(field.name);
          const isDateField =
            field.type === "date" ||
            datePickerFields.includes(field.name);

          return (
            <div className="col-md-2" key={field.name}>
              <div className="form-group mb-2">
                <label htmlFor={field.name} className="form-label">
                  {field.label}
                  {/* Always show required asterisk for required fields */}
                  {isRequired && (
                    <span className="text-danger">*</span>
                  )}
                </label>

                {field.type === "select" ? (
                  <select
                    id={field.name}
                    className={`form-select ${hasError ? "is-invalid" : ""
                      }`}
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={onChange}
                    required={field.required}
                    disabled={
                      field.readOnly ||
                      viewOnly ||
                      (field.name === "createUsing" && hasTableData)
                    }
                  >
                    {/* Show placeholder option for createUsing field */}
                    <option value="">Select</option>

                    {field.options?.map((opt) =>
                      typeof opt === "object" ? (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ) : (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      )
                    )}
                  </select>
                ) : field.type === "file" ? (
                  <div>
                    <input
                      id={field.name}
                      type="file"
                      className={`form-control ${hasError ? "is-invalid" : ""
                        }`}
                      accept={field.accept}
                      onChange={(e) => handleFileUpload(e, field.name)}
                      disabled={viewOnly || field.readOnly}
                    />
                    {formData[field.name] && (
                      <small className="text-muted">
                        Current file: {formData[field.name]}
                      </small>
                    )}
                  </div>
                ) : field.type === "number" ? (
                  <input
                    id={field.name}
                    type="number"
                    className={`form-control ${hasError ? "is-invalid" : ""
                      }`}
                    name={field.name}
                    value={formData[field.name] === 0 ? 0 : (formData[field.name] || "")}
                    onChange={onChange}
                    required={field.required}
                    disabled={viewOnly || field.readOnly}
                    step={field.step}
                    min={field.min}
                    max={field.max}
                  />
                ) : field.type === "partnerLookup" ? (
                  <PartnerLookupField
                    name={field.name}
                    value={formData[field.name] || ""}
                    disabled={viewOnly || field.readOnly}
                    required={isRequired}
                    error={hasError ? `${field.label} is required` : ""}
                    modalTitle={`Select ${field.label}`}
                    onSelect={(partner) => handlePartnerSelect(field.name, partner)}
                  />
                ) : field.type === "supplierLookup" || field.type === "customerLookup" || field.type === "shipFromLookup" ? (
                  <LookupField
                    name={field.name}
                    value={formData[field.name] || ""}
                    config={
                      field.type === "shipFromLookup"
                        ? shipFromLookupConfig
                        : field.type === "supplierLookup"
                          ? SUPPLIER_LOOKUP
                          : CUSTOMER_LOOKUP
                    }
                    modalTitle={field.type === "shipFromLookup" ? "Select Ship From" : undefined}
                    editable
                    disabled={viewOnly || field.readOnly}
                    required={isRequired}
                    error={hasError ? `${field.label} is required` : ""}
                    onChange={onChange}
                    onSelect={(rec) =>
                      field.type === "shipFromLookup"
                        ? applyShipFrom(field.name, rec)
                        : field.type === "supplierLookup"
                          ? applySupplier(field.name, rec)
                          : applyCustomer(field.name, rec)
                    }
                  />
                ) : field.type === "autosearch" ? (
                  <>
                          <PartnerAutoSearch
                      value={formData[field.name] || ""}
                      required={isRequired}
                      error={
                        hasError ? `${field.label} is required` : ""
                      }
                      disabled={viewOnly || field.readOnly}
                      onSelect={(partner) => handlePartnerSelect(field.name, partner)}
                    />

                    {hasError && (
                      <div className="invalid-feedback d-block">
                        {field.label} is required
                      </div>
                    )}
                  </>
                ) : field.searchable ? (
                  <div className="input-group">
                    <input
                      id={field.name}
                      type={field.type}
                      className={`form-control ${hasError ? "is-invalid" : ""
                        }`}
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={onChange}
                      onFocus={() => {
                        userInteractingRef.current = true;
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          userInteractingRef.current = false;
                        }, 500);
                      }}
                      required={field.required}
                      disabled={viewOnly || field.readOnly}
                      placeholder={
                        field.name === "SKUID"
                          ? ""
                          : field.name === "poNumber"
                            ? ""
                            : field.name === "externalPoNumber"
                              ? ""
                              : ""
                      }
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => handleSearch(field.name)}
                      disabled={viewOnly}
                      title={`Search for ${field.label}`}
                    >
                      <i className="bi bi-search"></i>
                    </button>
                  </div>
                ) : isDateTimeField || isDateField ? (
                  // Use CommonDateInput for all date fields
                  <CommonDateInput
                    id={field.name}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={onChange}
                    readOnly={viewOnly || field.readOnly}
                    disabled={field.disabled}
                    required={field.required}
                    className={hasError ? "is-invalid" : ""}
                    showTime={isDateTimeField}
                    allowPastDates={
                      field.name === "invoiceDate" ? true : false
                    }
                  />
                ) : (
                  // Default text input
                  <input
                    id={field.name}
                    type="text"
                    className={`form-control ${hasError ? "is-invalid" : ""
                      }`}
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={onChange}
                    required={field.required}
                    disabled={viewOnly || field.readOnly}
                  />
                )}

                {hasError && (
                  <div className="invalid-feedback">
                    {field.label} is required
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>



    </>
  );
}



